import { getPost, getReadMore, getReply } from '@app-core/post/actions'
import { NotFoundError } from '@features/errors/NotFoundError'
import { CleanPostDataProvider } from '@features/post/contexts/CleanPostDataContext'
import { PopularArticlesContextProvider } from '@features/post/contexts/PopularArticlesContext'
import { RecommendedArticlesContextProvider } from '@features/post/contexts/RecommendedArticlesContext'
import { useSendArticlePageView } from '@features/post/hooks/useSendArticlePageView'
import { compose, withBeacon, withRouter } from '@hocs'
import { getDefaultCategories } from '@libs/categories/getCategories'
import { ApiError } from '@libs/errorCode'
import { getApolloClient } from '@libs/getApolloClient'
import { withRNApp } from '@libs/RNApp'
import Config from '@public-config'
import { useAmp } from 'next/amp'
import ArticleAmpContainer from './ArticleAmpContainer'
import ArticleContainer from './ArticleContainer'
import PostHead from './components/PostHead'
import getPopularArticles from './libs/getPopularArticles'
import getRecommendedArticles from './libs/getRecommendedArticles'

import ShowADScript from '@components/ShowADScript'
import { getEventCategories } from '@libs/categories/getCategories'
import { getPostDescription } from '@libs/postTools'
import { htmlparser2 } from 'react-html-parser'
import { appendTableOfContents, preprocessNodes, removeCircularStructure } from './libs/postTools'

function ArticlePage({ hasError, recommendedArticles, popularArticles, nodes, ...props }) {
  const isAmp = useAmp()
  useSendArticlePageView(props.postId, hasError)

  if (hasError) return <NotFoundError isApp={!!props.isApp} context="post" />

  return (
    <CleanPostDataProvider nodes={nodes}>
      <PostHead />
      <ShowADScript />
      <RecommendedArticlesContextProvider value={recommendedArticles}>
        <PopularArticlesContextProvider value={popularArticles}>
          {isAmp ? <ArticleAmpContainer {...props} /> : <ArticleContainer {...props} isAmp={isAmp} />}
        </PopularArticlesContextProvider>
      </RecommendedArticlesContextProvider>
    </CleanPostDataProvider>
  )
}

// `getInitialProps` can only applied at page level, so put it here to support AMP
ArticlePage.getInitialProps = async ({ store, ...ctx }) => {
  const client = getApolloClient(ctx as any)
  const { forum, website, postID, amp, articleId } = ctx.query
  const categoryId = forum ? forum : website
  const category = forum ? `forum.${forum}` : website
  let postId = category && postID ? [category, postID].join('.') : null
  if (articleId) {
    postId = `draft.${articleId}`
  }

  let hasError
  let categories = []
  let recommendedArticles = []
  let popularArticles = []
  let nodes = []

  try {
    if (!postId) throw new ApiError(`missing postId`, 400)

    const [categoriesResult] = await Promise.all([
      getDefaultCategories(),
      store.dispatch(getPost(postId)),
    ])

    if (categoriesResult) {
      categories = categoriesResult
    }

    if (postId.includes('draft') === false) {
      await store.dispatch(getReadMore())
      if (amp === '1') await store.dispatch(getReply())
    }

    if (Config.recommendedArticlesEnabled) {
      recommendedArticles = await getRecommendedArticles({
        client,
        seedId: postId,
        limit: 6,
      })
    }

    if (Config.popularArticlesEnabled && categoryId) {
      popularArticles = await getPopularArticles({
        client,
        categoryId,
      })
    }

    const description = getPostDescription(store.getState().post.getIn(['post', 'data']).toJS())
    const categoryIds = categories.map((val) => val.id)
    nodes = htmlparser2.parseDOM(description, { decodeEntities: true })
    preprocessNodes(nodes, categoryIds)
    removeCircularStructure(nodes)
    appendTableOfContents(nodes)
  } catch (error) {
    console.error(error)
    hasError = true
    if (ctx.res) ctx.res.statusCode = error?.code ?? 500
  }

  const eventCategories = await getEventCategories(client)

  return {
    hasError,
    postId,
    categories,
    recommendedArticles,
    popularArticles,
    nodes,
    eventCategories,
  }
}

export default compose(withRNApp, withBeacon, withRouter)(ArticlePage)
